import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Button, Container, Table, Spinner, Modal, Form, InputGroup, Alert, Row, Col, Card, Badge, Collapse } from 'react-bootstrap';
import { FaEdit, FaTrash, FaTable, FaTh } from 'react-icons/fa';
import PhoneInput from 'react-phone-number-input';
import { getSuppliers, createSupplier, updateSupplier, deleteSupplier, getExpenses } from '../services/api';
import { trackAction } from '../services/analytics';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import Pagination from '../components/Pagination';
import { useDebounce } from '../hooks/useDebounce';
import usePermissions, { PERMISSIONS } from '../hooks/usePermissions';
import AuthContext from '../context/AuthContext';
import PageSizeSelector from '../components/PageSizeSelector';
import { handlePhoneBlurLogic } from '../utils/phoneUtils';
import { formatExpenseCategory } from '../utils/expenseCategories';
import useIsMobile from '../hooks/useIsMobile';

const ReadOnlyInput = ({ value, ...rest }) => (
    <Form.Control {...rest} value={value} readOnly />
);

const SuppliersPage = () => {
    const { user, business } = useContext(AuthContext);
    const { hasPermission } = usePermissions(user);
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const [suppliers, setSuppliers] = useState([]);

    const isSubscriptionActive = ['active', 'trialing'].includes(business?.subscription_status);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [modalData, setModalData] = useState({
        name: '',
        contact_name: '',
        email: '',
        phone_country_code: '',
        phone: '',
        address: '',
        tax_id: ''
    });
    const [editingSupplier, setEditingSupplier] = useState(null);

    // States for viewing supplier details and expenses
    const [viewingSupplier, setViewingSupplier] = useState(null);
    const [supplierExpenses, setSupplierExpenses] = useState([]);
    const [loadingExpenses, setLoadingExpenses] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('suppliersViewMode') || 'table');

    const toggleViewMode = (mode) => {
        setViewMode(mode);
        localStorage.setItem('suppliersViewMode', mode);
    };

    const fetchSuppliers = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getSuppliers({ page: currentPage, limit: pageSize, name: debouncedSearchTerm });
            setSuppliers(response.data.suppliers);
            setTotalPages(response.data.totalPages);
            setError('');
        } catch (error) {
            console.error(error);
            if (error.response && error.response.status === 403) {
                setError(error.response.data.message || t('suppliersPage.toasts.permissionError'));
            } else {
                toast.error(t('suppliersPage.toasts.fetchFailed'));
            }
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, debouncedSearchTerm, t]);

    useEffect(() => {
        if (hasPermission(PERMISSIONS.VIEW_SUPPLIERS)) {
            fetchSuppliers();
        } else {
            setLoading(false);
            setError(t('suppliersPage.toasts.permissionError'));
        }
    }, [fetchSuppliers, hasPermission, t]);

    const handleModalClose = () => {
        setShowModal(false);
        setEditingSupplier(null);
        setModalData({
            name: '',
            contact_name: '',
            email: '',
            phone_country_code: '',
            phone: '',
            address: '',
            tax_id: ''
        });
    };

    const handleCreateClick = () => {
        setEditingSupplier(null);
        setModalData({
            name: '',
            contact_name: '',
            email: '',
            phone_country_code: '',
            phone: '',
            address: '',
            tax_id: ''
        });
        setShowModal(true);
    };

    const handleEditClick = (supplier) => {
        setEditingSupplier(supplier);
        setModalData(supplier);
        setShowModal(true);
    };

    const handleDelete = async (supplierId) => {
        const result = await Swal.fire({
            title: t('areYouSure'),
            text: t('suppliersPage.toasts.deleteConfirm'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: 'var(--ev-action-danger-bg)',
            cancelButtonColor: 'var(--ev-bg-subtle)',
            confirmButtonText: t('yesDelete'),
            cancelButtonText: t('cancel')
        });

        if (result.isConfirmed) {
            const toastId = toast.loading(t('suppliersPage.toasts.deleting'));
            try {
                await deleteSupplier(supplierId);
                fetchSuppliers();
                trackAction('supplier_deleted');
                toast.success(t('suppliersPage.toasts.deleted'), { id: toastId });
            } catch (error) {
                toast.error(t('suppliersPage.toasts.deleteFailed'), { id: toastId });
            }
        }
    };

    const handleViewClick = async (supplier) => {
        setViewingSupplier(supplier);
        setLoadingExpenses(true);
        try {
            const response = await getExpenses({ supplier_id: supplier.supplier_id, pagination: 'false' });
            setSupplierExpenses(response.data);
        } catch (error) {
            console.error("Error fetching expenses:", error);
            toast.error(t('suppliersPage.toasts.fetchExpensesFailed'));
            setSupplierExpenses([]);
        } finally {
            setLoadingExpenses(false);
        }
    };

    const handleModalSubmit = async (e) => {
        e.preventDefault();
        const toastId = toast.loading(editingSupplier ? t('suppliersPage.toasts.updating') : t('suppliersPage.toasts.creating'));
        try {
            if (editingSupplier) {
                await updateSupplier(editingSupplier.supplier_id, modalData);
                trackAction('supplier_updated'); // sin PII: nada de nombre/teléfono/dirección
                toast.success(t('suppliersPage.toasts.updated'), { id: toastId });
            } else {
                await createSupplier(modalData);
                trackAction('supplier_created');
                toast.success(t('suppliersPage.toasts.created'), { id: toastId });
            }
            fetchSuppliers();
            handleModalClose();
        } catch (error) {
            trackAction('supplier_save_failed', { is_edit: !!editingSupplier });
            toast.error(editingSupplier ? t('suppliersPage.toasts.updateFailed') : t('suppliersPage.toasts.createFailed'), { id: toastId });
        }
    };

    const handleModalChange = (e) => {
        setModalData({ ...modalData, [e.target.name]: e.target.value });
    };

    const handleModalCountryCodeChange = (value) => {
        setModalData(prev => ({ ...prev, phone_country_code: value }));
    };

    const handlePhoneBlur = () => {
        handlePhoneBlurLogic(
            modalData.phone,
            modalData.phone_country_code,
            (newCountry) => setModalData(prev => ({ ...prev, phone_country_code: newCountry })),
            (newPhone) => setModalData(prev => ({ ...prev, phone: newPhone }))
        );
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (e) => {
        setPageSize(parseInt(e.target.value, 10));
        setCurrentPage(1);
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    return (
        <Container className="mt-4">
            <div className="d-flex justify-content-between align-items-center">
                <h2>{t('suppliersPage.title')}</h2>
                {hasPermission(PERMISSIONS.CREATE_SUPPLIERS) && (
                    <div title={!isSubscriptionActive ? t('subscriptionRequired') : !business?.plan_features?.suppliers_module ? t('common.featureNotAvailable') : ''} className="d-none d-md-block">
                        <Button
                            variant="primary"
                            onClick={handleCreateClick}
                            disabled={!isSubscriptionActive || !business?.plan_features?.suppliers_module}
                        >
                            {t('suppliersPage.newSupplierButton')}
                        </Button>
                    </div>
                )}
            </div>

            {hasPermission(PERMISSIONS.CREATE_SUPPLIERS) && (
                <div className="d-md-none position-fixed bottom-0 start-0 end-0 px-3 pt-3 bg-white shadow-lg" style={{ zIndex: 1000, borderTop: '1px solid #dee2e6', paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}>
                    <Button
                        variant="primary"
                        onClick={handleCreateClick}
                        disabled={!isSubscriptionActive || !business?.plan_features?.suppliers_module}
                        className="w-100"
                    >
                        {t('suppliersPage.newSupplierButton')}
                    </Button>
                </div>
            )}
            <p>{t('suppliersPage.subtitle')}</p>

            <div className="d-md-none mb-3">
                <Button variant="link" onClick={() => setShowFilters(!showFilters)} className="p-0 text-decoration-none">
                    {showFilters ? t('suppliersPage.hideFilters', 'Ocultar filtros') : t('suppliersPage.viewFilters', 'Ver filtros')}
                </Button>
            </div>

            <Collapse in={showFilters || window.innerWidth >= 768}>
                <div id="suppliers-filters">
                    <InputGroup className="mb-3">
                        <Form.Control
                            placeholder={t('suppliersPage.searchPlaceholder')}
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                    </InputGroup>
                </div>
            </Collapse>

            {error && <Alert variant="danger">{error}</Alert>}

            {/* View Mode Toggle */}
            <div className="d-flex justify-content-end align-items-center mb-3">
                <div
                    className="sales-view-toggle"
                    role="group"
                    aria-label={t('suppliersPage.viewToggle.label', 'Cambiar vista')}
                >
                    <button
                        id="suppliers-view-table"
                        className={`sales-view-toggle__btn${viewMode === 'table' ? ' sales-view-toggle__btn--active' : ''}`}
                        onClick={() => toggleViewMode('table')}
                        aria-pressed={viewMode === 'table'}
                        title={t('suppliersPage.viewToggle.tableView', 'Vista tabla')}
                        aria-label={t('suppliersPage.viewToggle.tableView', 'Vista tabla')}
                    >
                        <FaTable aria-hidden="true" />
                        <span className="sales-view-toggle__label">{t('suppliersPage.viewToggle.tableView', 'Tabla')}</span>
                    </button>
                    <button
                        id="suppliers-view-cards"
                        className={`sales-view-toggle__btn${viewMode === 'cards' ? ' sales-view-toggle__btn--active' : ''}`}
                        onClick={() => toggleViewMode('cards')}
                        aria-pressed={viewMode === 'cards'}
                        title={t('suppliersPage.viewToggle.cardsView', 'Vista tarjetas')}
                        aria-label={t('suppliersPage.viewToggle.cardsView', 'Vista tarjetas')}
                    >
                        <FaTh aria-hidden="true" />
                        <span className="sales-view-toggle__label">{t('suppliersPage.viewToggle.cardsView', 'Tarjetas')}</span>
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="text-center"><Spinner animation="border" /></div>
            ) : viewMode === 'table' ? (
                <div className="modern-table-wrapper mt-1">
                    <Table hover responsive className="modern-table">
                        <thead>
                            <tr>
                                <th>{t('suppliersPage.table.name')}</th>
                                <th>{t('suppliersPage.table.contactName')}</th>
                                <th>{t('suppliersPage.table.email')}</th>
                                <th>{t('suppliersPage.table.phone')}</th>
                                <th>{t('suppliersPage.table.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {suppliers.length > 0 ? suppliers.map(supplier => (
                                <tr key={supplier.supplier_id}>
                                    <td>{supplier.name}</td>
                                    <td>{supplier.contact_name}</td>
                                    <td>{supplier.email}</td>
                                    <td>{`${supplier.phone_country_code || ''} ${supplier.phone || ''}`}</td>
                                    <td className="text-center">
                                        <div className="btn-actions">
                                            <Button variant="info" size="sm" onClick={() => handleViewClick(supplier)} title={t('suppliersPage.viewButton')}>
                                                <i className="bi bi-eye"></i>
                                            </Button>
                                            {hasPermission(PERMISSIONS.EDIT_SUPPLIERS) && (
                                                <Button
                                                    variant="secondary"
                                                    size="sm"
                                                    onClick={() => handleEditClick(supplier)}
                                                    title={t('suppliersPage.editButton')}
                                                    aria-label={t('suppliersPage.editButton')}
                                                >
                                                    <FaEdit />
                                                </Button>
                                            )}
                                            {hasPermission(PERMISSIONS.DELETE_SUPPLIERS) && (
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => handleDelete(supplier.supplier_id)}
                                                    title={t('suppliersPage.deleteButton')}
                                                    aria-label={t('suppliersPage.deleteButton')}
                                                >
                                                    <FaTrash />
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan="5" className="text-center py-4 text-muted">{t('suppliersPage.noSuppliers')}</td></tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            ) : (
                /* ── Cards view ── */
                <div>
                    {suppliers.length === 0 ? (
                        <div className="text-center py-5 text-muted">{t('suppliersPage.noSuppliers')}</div>
                    ) : (
                        <div className="sales-cards-grid">
                            {suppliers.map(supplier => (
                                <div key={supplier.supplier_id} className="contact-card">
                                    <div className="contact-card__header">
                                        <span className="contact-card__name" title={supplier.name}>{supplier.name}</span>
                                        {supplier.contact_name && (
                                            <span className="contact-card__subtitle">{supplier.contact_name}</span>
                                        )}
                                    </div>
                                    <div className="contact-card__info">
                                        {supplier.email && (
                                            <div className="contact-card__info-row">
                                                <i className="bi bi-envelope contact-card__info-icon"></i>
                                                <span className="contact-card__info-value">{supplier.email}</span>
                                            </div>
                                        )}
                                        {(supplier.phone || supplier.phone_country_code) && (
                                            <div className="contact-card__info-row">
                                                <i className="bi bi-telephone contact-card__info-icon"></i>
                                                <span className="contact-card__info-value">{`${supplier.phone_country_code || ''} ${supplier.phone || ''}`.trim()}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="contact-card__actions btn-actions">
                                        <Button variant="info" size="sm" onClick={() => handleViewClick(supplier)} title={t('suppliersPage.viewButton')}>
                                            <i className="bi bi-eye"></i>
                                        </Button>
                                        {hasPermission(PERMISSIONS.EDIT_SUPPLIERS) && (
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => handleEditClick(supplier)}
                                                title={t('suppliersPage.editButton')}
                                                aria-label={t('suppliersPage.editButton')}
                                            >
                                                <FaEdit />
                                            </Button>
                                        )}
                                        {hasPermission(PERMISSIONS.DELETE_SUPPLIERS) && (
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => handleDelete(supplier.supplier_id)}
                                                title={t('suppliersPage.deleteButton')}
                                                aria-label={t('suppliersPage.deleteButton')}
                                            >
                                                <FaTrash />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <div className="d-flex justify-content-between align-items-center mt-3">
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
                <PageSizeSelector pageSize={pageSize} onPageSizeChange={handlePageSizeChange} />
            </div>

            <Modal show={showModal} onHide={handleModalClose} backdrop={isMobile ? 'static' : true}>
                <Modal.Header closeButton>
                    <Modal.Title>{editingSupplier ? t('suppliersPage.modal.editTitle') : t('suppliersPage.modal.createTitle')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleModalSubmit}>
                    <fieldset disabled={editingSupplier && !hasPermission(PERMISSIONS.EDIT_SUPPLIERS)}>
                        <Modal.Body>
                            <React.Fragment>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('suppliersPage.modal.nameLabel')}</Form.Label>
                                    <Form.Control type="text" name="name" value={modalData.name} onChange={handleModalChange} required />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('suppliersPage.modal.contactNameLabel')}</Form.Label>
                                    <Form.Control type="text" name="contact_name" value={modalData.contact_name || ''} onChange={handleModalChange} />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('suppliersPage.modal.emailLabel')}</Form.Label>
                                    <Form.Control type="email" name="email" value={modalData.email || ''} onChange={handleModalChange} />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('suppliersPage.modal.phoneLabel')}</Form.Label>
                                    <Row>
                                        <Col xs={5} md={4}>
                                            <PhoneInput
                                                value={modalData.phone_country_code}
                                                onChange={handleModalCountryCodeChange}
                                                numberInputComponent={ReadOnlyInput}
                                                className="phone-input-custom"
                                                defaultCountry="CO"
                                                international
                                            />
                                        </Col>
                                        <Col xs={7} md={8}>
                                            <Form.Control
                                                type="tel"
                                                name="phone"
                                                value={modalData.phone || ''}
                                                onChange={handleModalChange}
                                                onBlur={handlePhoneBlur}
                                            />
                                        </Col>
                                    </Row>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('suppliersPage.modal.addressLabel')}</Form.Label>
                                    <Form.Control as="textarea" rows={3} name="address" value={modalData.address || ''} onChange={handleModalChange} />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('suppliersPage.modal.taxIdLabel')}</Form.Label>
                                    <Form.Control type="text" name="tax_id" value={modalData.tax_id || ''} onChange={handleModalChange} />
                                </Form.Group>
                            </React.Fragment>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleModalClose}>{t('suppliersPage.modal.closeButton')}</Button>
                            {((editingSupplier && hasPermission(PERMISSIONS.EDIT_SUPPLIERS)) || (!editingSupplier && hasPermission(PERMISSIONS.CREATE_SUPPLIERS))) &&
                                <Button variant="primary" type="submit">{t('suppliersPage.modal.saveButton')}</Button>
                            }
                        </Modal.Footer>
                    </fieldset>
                </Form>
            </Modal>

            {/* Supplier Details & Expenses History Modal */}
            <Modal show={!!viewingSupplier} onHide={() => setViewingSupplier(null)} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>{viewingSupplier?.name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row>
                        <Col md={6}>
                            <h5>{t('suppliersPage.detailsTitle')}</h5>
                            <p><strong>{t('suppliersPage.table.contactName')}:</strong> {viewingSupplier?.contact_name || t('common.notAvailable')}</p>
                            <p><strong>{t('suppliersPage.table.email')}:</strong> {viewingSupplier?.email || t('common.notAvailable')}</p>
                            <p><strong>{t('suppliersPage.table.phone')}:</strong> {`${viewingSupplier?.phone_country_code || ''} ${viewingSupplier?.phone || ''}`.trim() || t('common.notAvailable')}</p>
                            <p><strong>{t('suppliersPage.modal.addressLabel')}:</strong> {viewingSupplier?.address || t('common.notAvailable')}</p>
                            <p><strong>{t('suppliersPage.modal.taxIdLabel')}:</strong> {viewingSupplier?.tax_id || t('common.notAvailable')}</p>
                        </Col>
                        <Col md={6}>
                            <h5>{t('suppliersPage.expensesHistoryTitle')}</h5>
                            {loadingExpenses ? (
                                <div className="text-center"><Spinner animation="border" size="sm" /></div>
                            ) : supplierExpenses.length > 0 ? (
                                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    {supplierExpenses.map(expense => (
                                        <Card key={expense.expense_id} className="mb-3 shadow-sm border-0">
                                            <Card.Body>
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <div>
                                                        <h6 className="mb-1 fw-bold text-primary">
                                                            {formatExpenseCategory(expense.category, t)}
                                                        </h6>
                                                        <small className="text-muted">
                                                            <i className="bi bi-calendar-event me-1"></i>
                                                            {new Date(expense.date).toLocaleDateString()}
                                                        </small>
                                                    </div>
                                                    <Badge bg="secondary" pill>
                                                        {expense.payment_method}
                                                    </Badge>
                                                </div>
                                                {expense.description && (
                                                    <p className="text-muted small mb-2">{expense.description}</p>
                                                )}
                                                <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
                                                    <span className="text-muted small">{t('suppliersPage.expenseAmount')}</span>
                                                    <span className="fw-bold fs-5 text-dark">
                                                        ${parseFloat(expense.amount).toLocaleString()}
                                                    </span>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    ))}
                                </div>
                            ) : (
                                <p>{t('suppliersPage.noExpensesFound')}</p>
                            )}
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setViewingSupplier(null)}>{t('suppliersPage.modal.closeButton')}</Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default SuppliersPage;
